export enum ZOLL_USER_ROLES {
  ADMIN = 'custadmin',
  USER = 'user',
  ZOLL_ADMIN = 'zolladmin',
}

export type RoleLike = string | ZOLL_USER_ROLES;

export enum ROLE {
  ANONYMOUS = 0,
  USER = 1,
  ADMIN = 50,
  ZOLL_ADMIN = 100,
}

// Convert BE roles to weight based FE roles.
const map: Record<string | ZOLL_USER_ROLES, ROLE> = {
  [ZOLL_USER_ROLES.ADMIN]: ROLE.ADMIN,
  [ZOLL_USER_ROLES.USER]: ROLE.USER,
  [ZOLL_USER_ROLES.ZOLL_ADMIN]: ROLE.ZOLL_ADMIN,
};

export class Roles<T = RoleLike> extends Array<T> {
  static create(roles?: RoleLike[]) {
    return new this(...(roles ?? []));
  }

  has(role?: T): boolean {
    if (role === undefined) {
      return false;
    }

    return this.includes(role);
  }

  hasAll(roles?: T[]): boolean {
    if (roles === undefined) {
      return true;
    }

    return roles.every((role) => this.has(role));
  }

  isAdmin(): boolean {
    return this.min(ROLE.ADMIN);
  }

  isAnonymous(): boolean {
    return this.max(ROLE.ANONYMOUS);
  }

  isUser(): boolean {
    return this.min(ROLE.USER);
  }

  isZollAdmin(): boolean {
    return this.min(ROLE.ZOLL_ADMIN);
  }

  max(role?: ROLE): boolean {
    return this.weight() <= (role ?? ROLE.ANONYMOUS);
  }

  min(role?: ROLE): boolean {
    return this.weight() >= (role ?? ROLE.ANONYMOUS);
  }

  weight(): ROLE {
    const weights = [...this.slice()].map((role) => Number(map[role]));
    return weights.length
      ? Math.max(...weights)
      : ROLE.ANONYMOUS;
  }
}
